import React from 'react';
import Modal from '../Modal';
import { Produit } from '../../types/weighing';
import { 
  CubeIcon, 
  ScaleIcon, 
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { Package } from 'lucide-react';

interface ProductDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Produit;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({ isOpen, onClose, product }) => {
  const getTypeDisplay = (type: string) => {
    const types: { [key: string]: string } = {
      'cereales': 'Céréales',
      'legumes': 'Légumes',
      'fruits': 'Fruits',
      'autres': 'Autres'
    };
    return types[type] || type;
  };

  const getConditionnementDisplay = (conditionnement: string) => {
    const conditionnements: { [key: string]: string } = {
      'sac': 'Sac',
      'carton': 'Carton',
      'bidon': 'Bidon',
      'palette': 'Palette',
      'autre': 'Autre'
    };
    return conditionnements[conditionnement] || conditionnement;
  };

  const getStatusDisplay = (statut: string) => {
    return statut === 'actif' ? 'Actif' : 'Inactif';
  };

  const getStatusIcon = (statut: string) => {
    return statut === 'actif' ? (
      <CheckCircleIcon className="w-5 h-5 text-green-600" />
    ) : (
      <XCircleIcon className="w-5 h-5 text-red-600" />
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Détails du produit" size="lg">
      <div className="space-y-6">
        {/* En-tête avec nom et statut */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <CubeIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{product.nom}</h3>
              <p className="text-sm text-gray-600">ID: {product.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusIcon(product.statut || 'actif')}
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              (product.statut || 'actif') === 'actif' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {getStatusDisplay(product.statut || 'actif')}
            </span>
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Type et Description */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type de produit</label>
              <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                <CubeIcon className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  {getTypeDisplay(product.type || 'cereales')}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  {product.description || 'Aucune description disponible'}
                </p>
              </div>
            </div>
          </div>

          {/* Poids et Conditionnement */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Poids moyen</label>
              <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
                <ScaleIcon className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  {product.poids_moyen ? `${product.poids_moyen} kg` : 'Non défini'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Conditionnement</label>
              <div className="flex items-center space-x-2 p-3 bg-purple-50 rounded-lg">
                <Package className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">
                  {getConditionnementDisplay(typeof product.conditionnement === 'number' ? 'sac' : (product.conditionnement || 'sac'))}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Informations supplémentaires */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Informations système</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Créé le:</span>
              <span className="text-gray-900">
                {product.created_at ? new Date(product.created_at).toLocaleDateString('fr-FR') : 'N/A'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Modifié le:</span>
              <span className="text-gray-900">
                {product.updated_at ? new Date(product.updated_at).toLocaleDateString('fr-FR') : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Bouton de fermeture */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ProductDetailsModal;
